import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './ClientPortal.css';
import ClientLoginPage from './ClientLoginPage';
import ClientLiveReport from './ClientDailyReport';
import ClientDailyReports from './ClientDailyReports';
import ClientPermitApplications from './ClientPermitApplications';
import ClientPermitSearch from './ClientPermitSearch';
import ClientTmdaApplication from './ClientTmdaApplication';

// ── Auth helpers ──────────────────────────────────────────────────────────────
const TOKEN_KEY = 'cp_token';
const REFRESH_KEY = 'cp_refresh_token';
const USER_KEY = 'cp_user';

export const cpGetHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
    'Content-Type': 'application/json',
});

export const cpGetAuthHeader = () => ({
    'Authorization': `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
});

// ── Navigation config ─────────────────────────────────────────────────────────
const NAV = [
    {
        group: 'Reports',
        items: [
            { key: 'live-report', label: 'Live Report', icon: 'fa-solid fa-table', path: 'live-report', module: 'CLIENT LIVE REPORT' },
            { key: 'daily-report', label: 'Daily Report', icon: 'fa-solid fa-file-pdf', path: 'daily-report', module: 'CLIENT DAILY REPORT' },
        ],
    },
    {
        group: 'Permits',
        items: [
            { key: 'permit-applications', label: 'Permit Applications', icon: 'fa-solid fa-id-card', path: 'permit-applications', module: 'CLIENT PERMIT APPLICATIONS' },
            { key: 'permit-search', label: 'Required Permit Search', icon: 'fa-solid fa-magnifying-glass', path: 'permit-search', module: 'CLIENT PERMIT SEARCH' },
            { key: 'tmda-application', label: 'TMDA Permit Application', icon: 'fa-solid fa-file-medical', path: 'tmda-application', module: 'CLIENT TMDA APPLICATION' },
        ],
    },
];

// ── Sidebar ───────────────────────────────────────────────────────────────────
const Sidebar = ({ collapsed, permissions }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (path) => location.pathname.includes(`/client/${path}`);
    const canView = (module) => permissions && permissions[module]?.can_view === true;

    const filteredNav = NAV.map(group => ({
        ...group,
        items: group.items.filter(item => canView(item.module))
    })).filter(group => group.items.length > 0);

    return (
        <aside className={`cp-sidebar${collapsed ? ' collapsed' : ''}`}>
            <div className="cp-sidebar-logo">
                <i className="fa-solid fa-building-user logo-icon"></i>
                <span>K &amp; K Client Portal</span>
            </div>

            <nav className="cp-nav">
                {filteredNav.map(group => (
                    <div className="cp-nav-group" key={group.group}>
                        <div className="cp-nav-group-title">{group.group}</div>
                        {group.items.map(item => (
                            <div
                                key={item.key}
                                className={`cp-nav-item${isActive(item.path) ? ' active' : ''}`}
                                onClick={() => navigate(`/client/${item.path}`)}
                                title={collapsed ? item.label : undefined}
                            >
                                <i className={`${item.icon} nav-icon`}></i>
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </nav>

            <div className="cp-sidebar-footer">
                <span className="company-name">K AND K COMPANY LIMITED</span>
                <span className="copyright">&copy; {new Date().getFullYear()}</span>
            </div>
        </aside>
    );
};

// ── Reset Password Modal ─────────────────────────────────────────────────────
const ClientResetPasswordModal = ({ onClose, onSuccess }) => {
    const [current, setCurrent] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!current || !newPw || !confirm) {
            setError('All fields are required.');
            return;
        }
        if (newPw !== confirm) {
            setError('New passwords do not match.');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch('/api/client/change-password/', {
                method: 'POST',
                headers: cpGetHeaders(),
                body: JSON.stringify({ old_password: current, new_password: newPw })
            });
            const data = await res.json();
            if (res.ok) {
                setSuccess('Password changed successfully.');
                setTimeout(onSuccess, 1200);
            } else {
                setError(Object.values(data).flat().join(' '));
            }
        } catch {
            setError('Connection error. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="cp-modal-overlay" onClick={onClose}>
            <div className="cp-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
                <div className="cp-modal-header">
                    <h3>Reset Password</h3>
                    <button className="cp-modal-close" onClick={onClose}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="cp-modal-body">
                        {error && <div className="cp-login-error" style={{ marginBottom: 12 }}>{error}</div>}
                        {success && <div style={{ marginBottom: 12, color: '#15803d', background: '#dcfce7', padding: '10px 14px', borderRadius: 6 }}>{success}</div>}
                        <div className="form-group" style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: 5 }}>Current Password</label>
                            <input type="password" className="tmda-input" value={current} onChange={e => setCurrent(e.target.value)} required />
                        </div>
                        <div className="form-group" style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: 5 }}>New Password</label>
                            <input type="password" className="tmda-input" value={newPw} onChange={e => setNewPw(e.target.value)} required />
                        </div>
                        <div className="form-group" style={{ marginBottom: 8 }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: 5 }}>Confirm New Password</label>
                            <input type="password" className="tmda-input" value={confirm} onChange={e => setConfirm(e.target.value)} required />
                        </div>
                    </div>
                    <div className="cp-modal-footer">
                        <button type="button" className="cp-btn cp-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="submit" className="cp-btn cp-btn-primary" disabled={saving || success}>{saving ? 'Changing…' : 'Change Password'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ── Main portal layout ────────────────────────────────────────────────────────
const ClientPortalLayout = ({ onLogout }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showReset, setShowReset] = useState(false);
    const menuRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    const pageTitles = {
        'live-report': 'Live Report',
        'daily-report': 'Daily Report',
        'permit-applications': 'Permit Applications',
        'permit-search': 'Required Permit Search',
        'tmda-application': 'TMDA Permit Application',
    };

    const currentKey = Object.keys(pageTitles).find(k => location.pathname.includes(k));
    const pageTitle = pageTitles[currentKey] || 'Client Portal';

    const canViewClient = (module) => permissions && permissions[module]?.can_view === true;
    const firstAllowed = useMemo(() => {
        const item = NAV.flatMap(g => g.items).find(i => canViewClient(i.module));
        return item ? item.path : 'daily-report';
    }, [permissions]);

    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) return;
        fetch('/api/client/me/', {
            headers: { 'Authorization': `Bearer ${token}` },
        }).then(r => r.ok ? r.json() : null).then(data => {
            if (data) {
                setUser(data);
                setPermissions(data.permissions || {});
                localStorage.setItem('cp_permissions', JSON.stringify(data.permissions || {}));
            }
        }).catch(() => {});
    }, []);

    useEffect(() => {
        const closeMenu = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        if (menuOpen) document.addEventListener('mousedown', closeMenu);
        return () => document.removeEventListener('mousedown', closeMenu);
    }, [menuOpen]);

    return (
        <div className="cp-root">
            <Sidebar collapsed={collapsed} permissions={permissions} />
            <div className={`cp-main${collapsed ? ' collapsed' : ''}`}>
                <header className="cp-topbar">
                    <button
                        className="cp-topbar-toggle"
                        onClick={() => setCollapsed(c => !c)}
                        title="Toggle sidebar"
                    >
                        <i className={`fa-solid ${collapsed ? 'fa-bars' : 'fa-bars'}`}></i>
                    </button>
                    <span className="cp-topbar-title">{pageTitle}</span>
                    <div className="cp-topbar-user" ref={menuRef} style={{ position: 'relative' }}>
                        <div
                            className="cp-user-trigger"
                            onClick={() => setMenuOpen(o => !o)}
                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        >
                            <i className="fa-solid fa-user" style={{ marginRight: 6, color: '#94a3b8' }}></i>
                            {user?.full_name || user?.username || ''}
                            <i className="fa-solid fa-caret-down" style={{ marginLeft: 8 }}></i>
                        </div>
                        {menuOpen && (
                            <div className="cp-user-dropdown">
                                <button className="cp-dropdown-item" onClick={() => { setMenuOpen(false); setShowReset(true); }}>
                                    <i className="fa-solid fa-lock"></i> Reset Password
                                </button>
                                <div className="cp-dropdown-divider"></div>
                                <button className="cp-dropdown-item" onClick={() => { setMenuOpen(false); onLogout(); }}>
                                    <i className="fa-solid fa-right-from-bracket"></i> Sign Out
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className="cp-content">
                    <Routes>
                        <Route path="live-report" element={<ClientLiveReport />} />
                        <Route path="daily-report" element={<ClientDailyReports />} />
                        <Route path="permit-applications" element={<ClientPermitApplications />} />
                        <Route path="permit-search" element={<ClientPermitSearch />} />
                        <Route path="tmda-application" element={<ClientTmdaApplication />} />
                        <Route path="*" element={<Navigate to="daily-report" replace />} />
                    </Routes>
                </main>
            </div>
            {showReset && (
                <ClientResetPasswordModal
                    onClose={() => setShowReset(false)}
                    onSuccess={() => {
                        setShowReset(false);
                        if (!location.pathname.includes(`/client/${firstAllowed}`)) {
                            navigate(`/client/${firstAllowed}`);
                        }
                    }}
                />
            )}
        </div>
    );
};

// ── Root client portal app ────────────────────────────────────────────────────
const ClientPortalApp = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authChecked, setAuthChecked] = useState(false);
    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem('cp_permissions');
        setIsAuthenticated(false);
        navigate('/client/login', { replace: true });
    }, [navigate]);

    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        const refresh = localStorage.getItem(REFRESH_KEY);
        if (!token || !refresh) {
            setAuthChecked(true);
            return;
        }
        // Try silent refresh
        fetch('/api/login/refresh/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh }),
        }).then(r => r.ok ? r.json() : null).then(data => {
            if (data?.access) {
                localStorage.setItem(TOKEN_KEY, data.access);
                if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
                setIsAuthenticated(true);
            } else {
                handleLogout();
            }
        }).catch(() => handleLogout()).finally(() => setAuthChecked(true));
    }, [handleLogout]);

    // Silent token refresh every 4 minutes
    useEffect(() => {
        if (!isAuthenticated) return;
        const iv = setInterval(() => {
            const refresh = localStorage.getItem(REFRESH_KEY);
            if (!refresh) return;
            fetch('/api/login/refresh/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh }),
            }).then(r => r.ok ? r.json() : null).then(data => {
                if (data?.access) {
                    localStorage.setItem(TOKEN_KEY, data.access);
                    if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
                } else {
                    handleLogout();
                }
            }).catch(() => handleLogout());
        }, 4 * 60 * 1000);
        return () => clearInterval(iv);
    }, [isAuthenticated, handleLogout]);

    const handleLoginSuccess = (data) => {
        localStorage.setItem(TOKEN_KEY, data.access);
        if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
        localStorage.setItem(USER_KEY, data.user);
        if (data.user?.permissions) {
            localStorage.setItem('cp_permissions', JSON.stringify(data.user.permissions));
        }
        setIsAuthenticated(true);
        navigate('/client/daily-report', { replace: true });
    };

    if (!authChecked) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                Loading...
            </div>
        );
    }

    return (
        <Routes>
            <Route
                path="login"
                element={
                    isAuthenticated
                        ? <Navigate to="/client/daily-report" replace />
                        : <ClientLoginPage onLoginSuccess={handleLoginSuccess} />
                }
            />
            <Route
                path="*"
                element={
                    isAuthenticated
                        ? <ClientPortalLayout onLogout={handleLogout} />
                        : <Navigate to="/client/login" replace />
                }
            />
        </Routes>
    );
};

export default ClientPortalApp;
